import React from "react";
import { createGUID } from 'utilities';

/**
* Default plain text error component
* (it is only used we do not specify the ErrorComponent parameter to tabLimitEnforcer function)
*/
const DefaultErrorComponent = ({ allowedTabs }) => (
  <div>
    The number of connections to ReVUe is limited to {allowedTabs}. Close other ReVUe windows and try again.
  </div>
);


/**
* Higher order component which decides what should be displayed to the user: Application or Error
*/
export function tabLimitEnforcer({
  ErrorComponent = DefaultErrorComponent, // component displayed when user tries to open too many tabs
  localStorageTimeout = 15 * 1000, // 15,000 milliseconds = 15 seconds. - In case the component will not succeed clearing the Storage on closing (desktop PC and a sudden power loss) - after this amount of time instances that were not removed from the storage will time out (will not be valid anymore)
  localStorageResetInterval = 10 * 1000, // 10,000 milliseconds = 10 seconds. - This is how often the above timeout is reset
  appName = "ReVUe", // has to be unique!
  allowedTabs = 5, // how many tabs can user open concurently
} = {}) {
  // returns a function that takes a component to wrap as a parameter
  return (WrappedComponent) => {
    // ...and returns another function component... (which decides what should be displayed on the screen)
    return (props) => {
      if (shouldTabBeAllowed(localStorageTimeout, localStorageResetInterval, appName, allowedTabs)) {
        return <WrappedComponent {...props} />;
      } else {
        return <ErrorComponent allowedTabs={allowedTabs} />;
      }
    };
  };
}

const shouldTabBeAllowed = function (localStorageTimeout, localStorageResetInterval, localStorageTabKey, allowedTabs) {
  var ItemType = {
    Session: 1,
    Local: 2,
  };

  function setCookie(name, value, days) {
    var expires = "";
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + (value || "") + expires + "; path=/";
  }

  function getCookie(name) {
    var nameEQ = name + "=";
    var ca = document.cookie.split(";");
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  function GetItem(itemtype) {
    var val = "";
    switch (itemtype) {
      case ItemType.Session:
        val = window.name;
        break;
      case ItemType.Local:
        val = decodeURIComponent(getCookie(localStorageTabKey));
        if (val == undefined) val = "";
        break;
    }
    return val;
  }

  function SetItem(itemtype, val) {
    switch (itemtype) {
      case ItemType.Session:
        window.name = val;
        break;
      case ItemType.Local:
        setCookie(localStorageTabKey, val);
        break;
    }
  }

  // returns current tabObj (object holding instances of opened tabs)
  function getTabObj() {
    var val = GetItem(ItemType.Local);
    var tabObj = (val === "" ? null : JSON.parse(val)) || null;
    return tabObj;
  }

  /**
   *
   * New opened tab is allowed when one of the following is true:
   * 1. Number of already opened instances is smaller than specified limit
   * 2. Number of already opened instances is equal to the specified limit but time out occured for at least one of them
   * 3. The GUID stored in the window.name matches with one of the GUIDs from tabObj (object holding instances of opened tabs) - SAME TAB REFRESHED CASE!
   * 
   * Each instance is represented as an object: { guid: ..., timestamp: ... }
   * For each of opened and allowed instances an interval will continue to update the timestamp (this allows to implement the timeout mechanism)
   * 
   */
  function isTabAllowed() {
    // check if window.name is set for this tab, if not then it is a new tab - create new GUID for this tab and set it
    // if tab already has window.name set, then we want to keep it - it means that tab is being refreshed
    var sessionGuid = GetItem(ItemType.Session) || createGUID();
    SetItem(ItemType.Session, sessionGuid);

    let tabObj = getTabObj();
    let isRefreshed = false;
    let thisTabIndex = (function () {
      // when tabObj was not initialized yet
      if (!tabObj) {
        return 0; // index of this tab is 0
      }
      // if it was initialized, then it has instances array initialized
      // index of the new instance will be the length of the list!
      let index = tabObj.instances.length;
      // unless sessionGuid already appears in the instances array
      // it means that someone just refreshed already opened tab (handles tab refresh case)
      tabObj.instances.forEach((instance, idx) => {
        if (instance?.guid === sessionGuid) {
          isRefreshed = true;
          index = idx;
        }
      });
      return index;
    })();

    // checks for the timeout of already opened tabs
    // if timeout occurs the below function also returns an index - of an instance that we should get rid of;
    // thisTabIndex will take index of timed out tab (the first one if there are many timed out tabs)
    // (it means that the new opened tab will take place of the timed out tab!)
    function checkIfTimeOutOccured() {
      let timeOutOccured = false;
      let instanceToSubstitute = -1;
      if (tabObj) {
        tabObj.instances.forEach((instance, index) => {
          if (instance && instance.timestamp < new Date().getTime() - localStorageTimeout) {
            timeOutOccured = true;
            instanceToSubstitute = index;
            return;
          }
        });
      }
      return { timeOutOccured, instanceToSubstitute };
    }
    // check for timeout
    const { timeOutOccured, instanceToSubstitute } = checkIfTimeOutOccured();
    // if thisTabIndex is greater than number of allowed tabs
    if (thisTabIndex > allowedTabs - 1) {
      // check if there was a timeout
      if (timeOutOccured) {
        // if there was a timeout then thisTabIndex steals index of timed out tab :)
        thisTabIndex = instanceToSubstitute;
      } else {
        // if there was no timeout on any tab it means that there are too many active tabs open!!!
        return false;
      }
    }

    // function which updates tabObj (object holding instances of tabs)
    const setTabObj = () => {
      let currentTabObj = getTabObj();
      let newTabObj = {
        ...currentTabObj,
      };
      if (!newTabObj.instances) {
        newTabObj.instances = [];
      }
      newTabObj.instances[thisTabIndex] = {
        guid: sessionGuid,
        timestamp: new Date().getTime(),
      };
      SetItem(ItemType.Local, JSON.stringify(newTabObj));
    };

    setTabObj(); // initial set
    setInterval(setTabObj, localStorageResetInterval); //every x interval refresh timestamp in cookie
    window.onunload = () => {
      // remove specific instance (of thisTabIndex) when closing the tab
      let currentTabObj = getTabObj();
      currentTabObj.instances.splice(thisTabIndex, 1); // remove tab of thisTabIndex from instances array
      if (tabObj.instances.length !== 0) {
        // if there are still open tabs then just update
        SetItem(ItemType.Local, JSON.stringify(tabObj));
      } else {
        // all instances were closed - clear everything
        SetItem(ItemType.Local, "");
        localStorage.removeItem(localStorageTabKey);
      }
    };
    return true; // allow to open tab of thisTabIndex
  }
  return isTabAllowed();
};